import styled, { css, keyframes } from 'styled-components';
import { CALLER_COLORS as C } from '../tokens';

// Shared styled-components for an incoming-call card. Font-family is intentionally
// inherited (app-wide Poppins); only the design's sizes/weights/colors are set.
// Sizes are kept compact so more cards fit the Caller ID panel at once.

// Expanding ring that radiates out and fades — mirrors the floating caller button
// so a freshly-arrived call draws the same "live incoming" attention.
const pulse = keyframes`
  0% {
    box-shadow: 0 0 0 0 rgba(251, 78, 0, 0.45);
  }
  70% {
    box-shadow: 0 0 0 12px rgba(251, 78, 0, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(251, 78, 0, 0);
  }
`;

export const CardShell = styled.div<{ $pulse?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  width: 100%;
  padding: 14px;
  background: #fff;
  border: 1px solid ${C.border};
  border-radius: 14px;
  box-shadow: ${C.cardShadow};
  ${({ $pulse }) => $pulse && css`
    border-color: ${C.accentOrange};
    animation: ${pulse} 1.8s ease-out infinite;
  `}
`;

export const CallLine = styled.div`
  display: flex;
  align-items: center;
  gap: 7px;
  color: ${C.green};
  font-size: 14px;
  font-weight: 600;
  img {
    width: 16px;
    height: 16px;
  }
`;

export const NameRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  padding-top: 6px;
`;

export const NameText = styled.span`
  color: ${C.navy};
  font-size: 18px;
  font-weight: 700;
`;

export const Badge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 9px;
  background: ${C.badgeBg};
  border-radius: 999px;
  color: ${C.green};
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
  img {
    width: 12px;
    height: 12px;
  }
`;

export const Phone = styled.div`
  width: 100%;
  color: ${C.gray};
  font-size: 15px;
`;

// Bordered container holding the expanded insight rows (replaces a bare divider
// so the top border + row spacing match the Figma in one place).
export const InsightsBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
  padding-top: 12px;
  border-top: 1px solid ${C.border};
`;

// One insight row: leading icon + text (shared by all expanded rows).
export const InsightRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 9px;
  > img {
    width: 16px;
    height: 16px;
    flex-shrink: 0;
    margin-top: 1px;
  }
`;

export const InsightText = styled.div`
  color: ${C.navy};
  font-size: 14px;
  line-height: 1.3;
  .due {
    color: ${C.orange};
    font-weight: 600;
  }
  .danger {
    color: ${C.red};
  }
  .inline-icon {
    width: 14px;
    height: 14px;
    vertical-align: middle;
    margin: 0 5px;
  }
`;

// Card body: caller info on the left, the SMS/BOOK actions stacked on the right.
export const CardBody = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  width: 100%;
`;

export const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1 1 auto;
  min-width: 0;
`;

export const Actions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex-shrink: 0;
`;

const ActionButton = styled.button`
  width: 120px;
  height: 42px;
  border-radius: 10px;
  font-size: 15px;
  font-weight: 700;
  cursor: pointer;
  &:active {
    opacity: 0.7;
  }
`;

export const SmsButton = styled(ActionButton)`
  background: #fff;
  border: 1px solid ${C.border};
  color: ${C.navy};
`;

export const BookButton = styled(ActionButton)`
  background: ${C.greenBtn};
  border: 1px solid ${C.greenBtnBorder};
  color: #fff;
`;

// Skip = loại một caller spam khỏi danh sách. Outline đỏ để phân biệt rõ
// với BOOK (nền xanh đặc) — cùng kích thước nhưng không cạnh tranh thị giác.
export const SkipButton = styled(ActionButton)`
  background: #fff;
  border: 1px solid ${C.red};
  color: ${C.red};
`;

export const ToggleRow = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;
  padding-top: 6px;
`;

export const ToggleChip = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 14px;
  background: ${C.lightBtnBg};
  border: 1px solid ${C.border};
  border-radius: 999px;
  color: ${C.gray};
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  &:active {
    opacity: 0.7;
  }
  img {
    width: 15px;
    height: 15px;
  }
`;
